/**
 * AVAILABLE LANGUAGES
 */
import { ICardTranslation } from "containers/Card/store/CardState";
import {
  englishCajitaLayout,
  englishCardAsyncForm,
  englishCardForm,
  englishCardSubscriptionAsyncForm,
  englishCashForm,
  englishPayoutsCashForm,
  englishTransferForm,
  englishTransferSubscriptionForm,
} from "root/shared/Languages/English";
import {
  portugueseCajitaLayout,
  portugueseCardAsyncForm,
  portugueseCardForm,
  portugueseCardSubscriptionAsyncForm,
  portugueseCashForm,
  portuguesePayoutsCashForm,
  portugueseTransferForm,
  portugueseTransferSubscriptionForm,
} from "root/shared/Languages/Portugues";
import {
  spanishCajitaLayout,
  spanishCardAsyncForm,
  spanishCardForm,
  spanishCardSubscriptionAsyncForm,
  spanishCashForm,
  spanishPayoutsCashForm,
  spanishTransferForm,
  spanishTransferSubscriptionForm,
} from "root/shared/Languages/Spanish";
import { IPayoutsCashTranslation } from "types/payouts_cash_form_props";
import { ITransferSubscriptionTranslation } from "types/transfer_subscriptions_form_props";

export const availableLanguages: ILanguage[] = [
  { name: "Español", code: "es" },
  { name: "English", code: "en" },
  { name: "Português", code: "br" },
];

export interface ILanguage {
  name: string;
  code: string;
}

/**
 * Interfaces for components in CajitaLayout
 */

export interface IPaymentMethods {
  card: string[];
  credit_card: string[];
  debit_card: string[];
  transfer: string[];
  transfer_subscription: string[];
  transfer_mxn: string[];
  cash: string[];
  payouts_cash: string[];
  card_async: string[];
  card_dynamic: string[];
  card_subscription_dynamic: string[];
  card_subscription_async: string[];
}

export interface IFooter {
  secureMessage: string[];
  learnMore: string[];
  changeLanguage: string[];
  cardDocumentation: string[];
  pseDocumentation: string[];
}

export interface ITextLanguages {
  en: string[];
  es: string[];
  br: string[];
}

export interface ICajitaLayoutTranslation {
  payButton: string[];
  payoutButton: string[];
  payinButton: string[];
  selectPaymentMethod: string[];
  selectYourBank: string[];
  changePaymentMethod: string[];
  subscriptionButton: string[];
  selectorMethods: IPaymentMethods;
  notAvailable: string[];
  notAvailableTransfer: string[];
  notAvailableCash: string[];
  notAvailableCardAsync: string[];
  notAvailableCardSubscriptionAsync: string[];
  overlayMessageTitle: string[];
  overlayMessageSubtitle: string[];
  cashHelperMessage: string[];
  transferHelperMessage: string[];
  payinTotalAmountMessage: string[];
  footer: IFooter;
  languages: ITextLanguages;
}

/**
 * Interface for cash form translation
 */
export interface ICashFormTranslation {
  nameInput: string[];
  lastNameInput: string[];
  identificationInput: string[];
  description: string[];
  email: string[];
}

/**
 * Interface for card form translation
 */
export interface ICardFormTranslation {
  cardHolder: string[];
  cardNumber: string[];
  expDate: string[];
  cardCVC: string[];
  email: string[];
  invalidOtpMessage: string[];
  otpDinersTitle: string[];
  otpKushkiTitle: string[];
  messageKushki: string[];
  messageDiners: string[];
  howItWorks: string[];
  otpTitle: string[];
  tryWithAnotherCard: string[];
  otpButtonText: string[];
  helpText: string[];
  exampleText: {
    forExample: string[];
    firstPhrase: string[];
    secondPhrase: string[];
  };
  transactionResume: string[];
  valueToPay: string[];
  numberOffees: string[];
  amountPerFee: string[];
  continue: string[];
  back: string[];
  deferredOptionsComponent: IDeferredOptionsTranslation;
  commission: {
    link: string[];
    title: string[];
    text: string[];
  };
  exceededRetries: {
    title: string[];
    description: string[];
    note: string[];
    button: string[];
  };
}

export interface IDeferredOptionsTranslation {
  deferredTypeSelectLabel: string[];
  monthsSelectLabel: string[];
  monthsOfGraceSelectLabel: string[];
  toggleTitle: string[];
}

/**
 * Interface for card-async form translation
 */

export interface ICardAsyncFormTranslation {
  emailInput: string[];
}

/**
 * Interface for card-subscription-async form translation
 */

export interface ICardSubscriptionAsyncFormTranslation {
  emailInput: string[];
  cardNumber: string[];
}

/**
 * Interface for transfer form translation
 */

export interface ITransferFormTranslation {
  documentEmailInput: string[];
  documentNumberInput: string[];
  paymentDescriptionInput: string[];
  bankListSelectLabel: string[];
  userTypeRadioGroupLabel: string[];
  userTypeRadioGroup: IPersonType;
}

export interface IPersonType {
  "0": string[];
  "1": string[];
}

// tslint:disable-next-line:valid-jsdoc
/**
 * Define translations in CajitaLayout
 */
export const getLayoutTranslations: () => ICajitaLayoutTranslation = (): ICajitaLayoutTranslation =>
  <ICajitaLayoutTranslation>(
    mergeLanguages(spanishCajitaLayout, [
      englishCajitaLayout,
      portugueseCajitaLayout,
    ])
  );

// tslint:disable-next-line:valid-jsdoc
/**
 * Define translations in CashForm
 */
export const getCashFormTranslations: () => ICashFormTranslation = (): ICashFormTranslation =>
  <ICashFormTranslation>(
    mergeLanguages(spanishCashForm, [englishCashForm, portugueseCashForm])
  );

// tslint:disable-next-line:valid-jsdoc
/**
 * Define translations in CardForm
 */
export const getCardFormTranslations: () => ICardTranslation = (): ICardTranslation =>
  <ICardTranslation>(
    mergeLanguages(spanishCardForm, [englishCardForm, portugueseCardForm])
  );

// tslint:disable-next-line:valid-jsdoc
/**
 * Define translations in CardAsyncForm
 */
export const getCardAsyncFormTranslations: () => ICardAsyncFormTranslation = (): ICardAsyncFormTranslation =>
  <ICardAsyncFormTranslation>(
    mergeLanguages(spanishCardAsyncForm, [
      englishCardAsyncForm,
      portugueseCardAsyncForm,
    ])
  );

// tslint:disable-next-line:valid-jsdoc
/**
 * Define translations in Transfer
 */
export const getTransferFormTranslations: () => ITransferFormTranslation = (): ITransferFormTranslation =>
  <ITransferFormTranslation>(
    mergeLanguages(spanishTransferForm, [
      englishTransferForm,
      portugueseTransferForm,
    ])
  );

// tslint:disable-next-line:valid-jsdoc
/**
 * Define translations in Transfer Subscription
 */
export const getTransferSubscriptionFormTranslations: () => ITransferSubscriptionTranslation = (): ITransferSubscriptionTranslation =>
  <ITransferSubscriptionTranslation>(
    mergeLanguages(spanishTransferSubscriptionForm, [
      englishTransferSubscriptionForm,
      portugueseTransferSubscriptionForm,
    ])
  );

// tslint:disable-next-line:valid-jsdoc
/**
 * Define translations in CardAsyncForm
 */
export const getCardSubscriptionAsyncFormTranslations: () => ICardSubscriptionAsyncFormTranslation = (): ICardSubscriptionAsyncFormTranslation =>
  <ICardSubscriptionAsyncFormTranslation>(
    mergeLanguages(spanishCardSubscriptionAsyncForm, [
      englishCardSubscriptionAsyncForm,
      portugueseCardSubscriptionAsyncForm,
    ])
  );

// tslint:disable-next-line:valid-jsdoc
/**
 * Define translations in Payouts Cash
 */
export const getPayoutsCashFormTranslations: () => IPayoutsCashTranslation = (): IPayoutsCashTranslation =>
  <IPayoutsCashTranslation>(
    mergeLanguages(spanishPayoutsCashForm, [
      englishPayoutsCashForm,
      portuguesePayoutsCashForm,
    ])
  );

// tslint:disable-next-line:valid-jsdoc
/**
 * Function to merge the translations
 * @param defaultLanguage: defaultLanguage to show
 * @param translateOptions: an array with the translations
 */

function mergeLanguages(
  defaultLanguage: object,
  translateOptions: object[]
): object {
  const result: object = defaultLanguage;

  translateOptions.forEach((translations: object) => {
    Object.keys(translations).forEach((key: string) => {
      if (Array.isArray(translations[key]))
        result[key] = result[key].concat(translations[key]);
      else {
        const subKeys: string[] = Object.keys(translations[key]);

        subKeys.forEach((subKey: string) => {
          result[key][subKey] = result[key][subKey].concat(
            translations[key][subKey]
          );
        });
      }

      return;
    });
  });

  return result;
}

export function isAvaliableLanguage(language: string): boolean {
  return (
    availableLanguages.filter((options: ILanguage) => options.code === language)
      .length !== 0
  );
}



// WEBPACK FOOTER //
// ./src/shared/MultiLanguage.ts